/**
 * <EmployeesHardDeleteDialog> — confirm hard-delete of a PENDING user.
 *
 * Phase-2 W1.T8. AlertDialog (Radix) — destructive confirm with strongly
 * worded copy: the row is removed permanently along with the open invite,
 * and any later activation attempt with the original token will fail.
 *
 * Wired via `useHardDeleteUserMutation` from `@/api/users`. The mutation
 * already drops the detail cache + invalidates `qk.users.all` on success
 * — the calling list refetches without manual wiring.
 *
 * Error surface:
 *   * 422 cannot_hard_delete_active — the row flipped to ACTIVE/ARCHIVED
 *     between the list render and the click; we close + toast info so
 *     the refetch corrects the UI.
 *   * 403 admin_cannot_archive_employee_tier — Admin clicked through on
 *     a Сотрудник row (the column factory should have hidden the item;
 *     defense in depth here keeps the message helpful).
 *   * Anything else — generic error toast.
 */
import * as React from 'react';

import type { UserDetail } from '@aris/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/fetcher';

import { useHardDeleteUserMutation } from '@/api/users';

export interface EmployeesHardDeleteDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user: UserDetail | null;
}

export function EmployeesHardDeleteDialog(
  props: EmployeesHardDeleteDialogProps,
): React.ReactElement | null {
  const { open, onOpenChange, user } = props;
  const deleteMutation = useHardDeleteUserMutation();

  const onConfirm = React.useCallback(async () => {
    if (!user) return;
    try {
      await deleteMutation.mutateAsync({ id: user.id });
      toast('Приглашение удалено', { variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && err.code === 'cannot_hard_delete_active') {
          toast('Статус пользователя изменился — обновите страницу и попробуйте снова.', {
            variant: 'info',
          });
          onOpenChange(false);
          return;
        }
        if (err.status === 403 && err.code === 'admin_cannot_archive_employee_tier') {
          toast('Только РО управляет приглашениями сотрудников.', { variant: 'error' });
          onOpenChange(false);
          return;
        }
        if (err.status === 404) {
          // Already gone — treat as success from the user's perspective.
          toast('Приглашение уже удалено', { variant: 'info' });
          onOpenChange(false);
          return;
        }
        toast(err.message, { variant: 'error' });
        return;
      }
      toast('Не удалось удалить приглашение.', { variant: 'error' });
    }
  }, [deleteMutation, onOpenChange, user]);

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="employees-hard-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="employees-hard-delete-title">
            Удалить приглашение для {user.fullName}?
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="employees-hard-delete-body">
            Это действие необратимо. Карточка пользователя и активное приглашение будут удалены
            полностью. Если сотрудник позже попытается активировать аккаунт по старой ссылке — она
            уже не будет работать.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="employees-hard-delete-cancel">Отмена</AlertDialogCancel>
          <AlertDialogAction
            data-testid="employees-hard-delete-confirm"
            onClick={(e) => {
              // Prevent the AlertDialog's default close behaviour — we close
              // ourselves on success / error so the busy state is visible.
              e.preventDefault();
              void onConfirm();
            }}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:opacity-90"
          >
            Удалить
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
